import { createTheme, alpha } from '@mui/material/styles';

/* ──────────────────────────────────────────────────────────────
   DESIGN-TOKENS — die EINZIGE Quelle für Farben.
   Seit D3 gibt es zwei Token-Reihen (hell/dunkel); die aktive liegt
   zur Laufzeit unter theme.palette.tokens. Komponenten gehen daher
   über das Theme (theme.palette.tokens.X bzw. palette-Keys), nie
   über direkte Importe aus dieser Datei.
   ────────────────────────────────────────────────────────────── */
const tokensLight = {
  // Marke (Design 3.0, Entscheidung C): Indigo als Hauptakzent —
  // ein Akzent mit Disziplin, Teal bleibt Stützfarbe (Logo-Punkt,
  // Vergleichsreihen in Charts).
  primary:      '#4F46E5',
  primaryDeep:  '#4038CE',
  primarySoft:  '#E9E8FC',
  accent:       '#0E9E97', // Teal (Markenfarbe, z. B. Logo-Punkt)
  accentDeep:   '#0B7A74',
  accentSoft:   '#DFF3F1',

  // Chart-Farbsystem (Alexanders Regel): VIER Ramps als ZWEI harmonisierte
  // Paare — pro Chart immer nur ein Paar (+ Grau), nie Familien mischen,
  // die nicht Partner sind. So bleibt jedes Chart in sich stimmig:
  //   Paar A (kühl):   ramp  (Indigo) + ramp2 (Blau)  → CRM & Projekte
  //   Paar B (frisch): ramp3 (Teal)   + ramp4 (Grün)  → Zeit, künftig Umsatz/Angebote
  // Die Teal-Trendlinie gehört zu Paar B; Semantik-Grün (success) bleibt
  // Chips/Status vorbehalten und taucht in Paar-A-Charts nicht mehr auf.
  ramp: {
    100: '#E9E8FC', 200: '#C9C8F7', 300: '#A5A3F2',
    500: '#5B54E8', 700: '#4038CE', 900: '#2B2489',
  },
  ramp2: {
    100: '#DBEAFE', 200: '#BFDBFE', 300: '#93C5FD',
    500: '#3B82F6', 700: '#1D4ED8', 900: '#1E3A8A',
  },
  ramp3: {
    100: '#DFF3F1', 200: '#BFE7E4', 300: '#8FD4CF',
    500: '#23A69E', 700: '#0B8F88', 900: '#075E59',
  },
  ramp4: {
    100: '#D1FAE5', 200: '#A7F3D0', 300: '#6EE7B7',
    500: '#10B981', 700: '#047857', 900: '#064E3B',
  },
  chartNeutral: '#C9CFD9', // „Verloren“/Restmengen in Charts

  // Neutrale (Design 3.0): dunklere Tinte für Kontrast, feinere Linien,
  // ruhigerer Grund — 90 % der Fläche entscheiden über „hochwertig“.
  ink:          '#14181F',
  ink2:         '#3A4354',
  muted:        '#647084',
  line:         '#E7EAF0',
  lineSoft:     '#F0F2F6',
  surface:      '#FFFFFF',
  surface2:     '#F0F2F6',
  ground:       '#F6F7FA',

  // Navigation 3.1 (Variante V1 „dunkle Leiste“, Alexanders Wahl):
  // Indigo-Anthrazit in BEIDEN Modi — dunkler Rahmen, heller Inhalt
  // (Linear-/Attio-Muster). Löst den N2-Akzent-Hauch aus D1 ab.
  sidebar:           '#1D2030',
  sidebarLine:       '#2A2E42',
  sidebarText:       '#C7CBE0',
  sidebarMuted:      '#8E96B8',
  sidebarHover:      'rgba(255,255,255,0.06)',
  sidebarActive:     'rgba(129,140,248,0.18)',
  sidebarActiveText: '#C7D2FE',

  // Semantik (bewusst getrennt vom Akzent) — tiefere Text-Töne; die
  // Pastell-Pills entstehen im Bestand per alpha(color, ~0.13) und
  // treffen mit diesen Werten die Soft-Grund/Dunkel-Text-Paare.
  success:      '#15803D',
  warning:      '#B45309',
  danger:       '#B91C1C',
  info:         '#4F46E5',
  gold:         '#A16207',

  // Tabellen & Rollbalken
  tableHead:        '#FAFBFC',
  hoverRow:         '#F4F6F9',
  selectedHover:    '#DCDBF3',
  scrollThumb:      '#C9CFD9',
  scrollThumbHover: '#AEB7C6',
};

/* Dunkle Token-Reihe (D3): kein invertiertes Theme, sondern ein eigenes —
   warmes Anthrazit statt Schwarz, Flächen hellen nach oben ab (Tiefe über
   Helligkeit statt Schatten), Akzente aufgehellt und leicht entsättigt.
   Die Ramps sind GESPIEGELT (100 ↔ 900): „intensiver“ heißt im Dunkeln
   „heller“ — dadurch funktionieren Chart-Stufen, Chip-Textfarben und
   Fortschrittsbalken ohne Sonderlogik in den Komponenten. */
const tokensDark = {
  primary:      '#818CF8', // Indigo aufgehellt — Buttons tragen dunkle Schrift
  primaryDeep:  '#A5B4FC', // „Vertiefen“ heißt im Dunkeln: aufhellen
  primarySoft:  '#272B52',
  accent:       '#4EC8C0',
  accentDeep:   '#7DD8D2',
  accentSoft:   '#12312F',

  ramp: {
    100: '#2B2489', 200: '#4038CE', 300: '#5B54E8',
    500: '#A5A3F2', 700: '#C9C8F7', 900: '#E9E8FC',
  },
  ramp2: {
    100: '#1E3A8A', 200: '#1D4ED8', 300: '#3B82F6',
    500: '#93C5FD', 700: '#BFDBFE', 900: '#DBEAFE',
  },
  ramp3: {
    100: '#075E59', 200: '#0B8F88', 300: '#23A69E',
    500: '#8FD4CF', 700: '#BFE7E4', 900: '#DFF3F1',
  },
  ramp4: {
    100: '#064E3B', 200: '#047857', 300: '#10B981',
    500: '#6EE7B7', 700: '#A7F3D0', 900: '#D1FAE5',
  },
  chartNeutral: '#4A5261',

  ink:          '#E9ECF2',
  ink2:         '#C2C9D6',
  muted:        '#98A2B5',
  line:         '#262D39',
  lineSoft:     '#222834',
  surface:      '#171B22',
  surface2:     '#1E242E',
  ground:       '#101318',

  // V1 im Dunkeln: eine Nuance vom Grund abgesetzt, sonst identisch —
  // die Leiste ist in beiden Modi dasselbe Gesicht.
  sidebar:           '#14161F',
  sidebarLine:       '#1F2330',
  sidebarText:       '#C2C9D6',
  sidebarMuted:      '#8E96B8',
  sidebarHover:      'rgba(255,255,255,0.06)',
  sidebarActive:     'rgba(129,140,248,0.16)',
  sidebarActiveText: '#C7D2FE',

  success:      '#4CC38A',
  warning:      '#E2A33B',
  danger:       '#F07A72',
  info:         '#818CF8',
  gold:         '#D9A73E',

  tableHead:        '#1A1F28',
  hoverRow:         '#1C222C',
  selectedHover:    '#2E3260',
  scrollThumb:      '#39414F',
  scrollThumbHover: '#4A5261',
};

/* Palette je Farbschema — beide Schemata entstehen aus derselben Fabrik,
   damit kein Custom-Key nur in einem Modus existiert. */
const buildPalette = (tokens, mode) => {
  const dark = mode === 'dark';

  // Status → eine Bedeutung, eine Farbe (früher über 5 Objekte verstreut)
  const statusColors = {
    yellow: tokens.gold,
    blue:   tokens.primary,
    orange: tokens.warning,
    green:  tokens.success,
    red:    tokens.danger,
    grey:   tokens.muted,
  };

  return {
    primary:   { main: tokens.primary, light: tokens.primarySoft, dark: tokens.primaryDeep, contrastText: dark ? '#101318' : '#FFFFFF' },
    secondary: { main: tokens.accent,  light: tokens.accentSoft,  dark: tokens.accentDeep,  contrastText: dark ? '#101318' : '#FFFFFF' },
    error:     { main: tokens.danger },
    warning:   { main: tokens.warning },
    success:   { main: tokens.success },
    info:      { main: tokens.info },
    text:      { primary: tokens.ink, secondary: tokens.muted },
    divider:   tokens.line,
    background: {
      default: tokens.ground,
      paper: tokens.surface,
      highlightPanel: tokens.surface,
    },

    // Design-Tokens direkt verfügbar (z. B. theme.palette.tokens.accent)
    tokens,

    // ── Tonwertskalen (beibehalten für Kompatibilität) ──────────
    newPrimaryTest: {
      main: '#005A92', t0: '#000000', t10: '#001D34', t20: '#003355', t30: '#004A79',
      t40: '#15629A', t50: '#397BB5', t60: '#5695D1', t70: '#72B0ED', t80: '#9ACBFF',
      t90: '#CFE4FF', t95: '#E9F1FF', t100: '#FFFFFF',
    },
    newSecondaryTest: {
      main: '#6B7889', t0: '#000000', t10: '#0F1D2A', t20: '#243240', t30: '#3B4857',
      t40: '#526070', t50: '#6B7889', t60: '#8492A3', t70: '#9EACBF', t80: '#BAC8DA',
      t90: '#D6E4F7', t95: '#E9F1FF', t100: '#FFFFFF',
    },
    newTertiaryTest: {
      main: '#77729E', t0: '#000000', t10: '#1A163C', t20: '#2F2B52', t30: '#46426A',
      t40: '#5E5983', t50: '#77729E', t60: '#918CB9', t70: '#ACA6D5', t80: '#C7C1F1',
      t90: '#E4DFFF', t95: '#F3EEFF', t100: '#FFFFFF',
    },
    newNeutralTest: {
      main: '#F1F3F4', t0: '#000000', t10: '#191C1D', t20: '#2E3132', t30: '#444748',
      t40: '#5C5F60', t50: '#747779', t60: '#8E9192', t70: '#A9ACAD', t80: '#C4C7C8',
      t90: '#E1E3E4', t95: '#EFF1F2', t100: '#FFFFFF',
    },
    statusSuccess: {
      main: '#2E7D4F', t0: '#000000', t10: '#0B1F14', t20: '#163825', t30: '#215136',
      t40: '#2E7D4F', t50: '#3E9664', t60: '#56B07D', t70: '#73C99A', t80: '#9BE3B9',
      t90: '#CFF5DB', t95: '#E8FBF0', t100: '#FFFFFF',
    },
    statusWarning: {
      main: '#B88A00', t0: '#000000', t10: '#2A1F00', t20: '#473400', t30: '#664A00',
      t40: '#8A6500', t50: '#B88A00', t60: '#D6A826', t70: '#F0C34D', t80: '#FFDB7A',
      t90: '#FFF0B8', t95: '#FFF8DE', t100: '#FFFFFF',
    },
    statusInfo: {
      main: '#C45A1A', t0: '#000000', t10: '#2A1307', t20: '#4A240F', t30: '#6B3617',
      t40: '#914A1F', t50: '#C45A1A', t60: '#E17333', t70: '#F18E52', t80: '#F9B08A',
      t90: '#FDD8C6', t95: '#FEF0E8', t100: '#FFFFFF',
    },
    statusError: {
      main: '#B3261E', t0: '#000000', t10: '#2C0B0A', t20: '#4A1715', t30: '#6B2320',
      t40: '#8C2F2A', t50: '#B3261E', t60: '#D1453D', t70: '#E46961', t80: '#F2A19C',
      t90: '#F9D4D1', t95: '#FDEDEC', t100: '#FFFFFF',
    },

    // ── Custom-Keys (Werte auf das neue System harmonisiert) ────
    interaction: {
      call:    tokens.primary,
      email:   tokens.gold,
      task:    tokens.success,
      meeting: tokens.accent, // Teal-Stütze — Violett kollidierte mit dem Indigo-Akzent
      note:    tokens.muted,
    },
    statusColors: { ...statusColors },
    leadStatus: {
      open: statusColors.yellow, contacted: statusColors.blue, interested: statusColors.orange,
      closedWon: statusColors.green, declined: statusColors.red, unknown: statusColors.grey,
    },
    projectStatus: {
      planning: statusColors.yellow, inProgress: statusColors.blue, onHold: statusColors.orange,
      completed: statusColors.green, cancelled: statusColors.red, unknown: statusColors.grey,
    },
    leadScore: {
      cold: statusColors.blue, warm: statusColors.orange, hot: statusColors.red,
      unknown: statusColors.grey, cool: statusColors.blue, qualified: statusColors.green,
    },
    agencySubmissionsLeadScore: {
      cold: dark ? '#78909C' : '#90A4AE', cool: tokens.primary, qualified: tokens.success,
      warm: tokens.warning, hot: tokens.danger,
    },
    leadConversionPanel: dark ? {
      declined: { bg: '#2A1514', border: '#3C1F1D', iconBg: tokens.danger,  title: '#F5A9A3', text: tokens.danger },
      won:      { bg: '#122820', border: '#1B3A2D', iconBg: tokens.success, title: '#8FE0B8', text: tokens.success },
      buttons:  { primaryHover: 'rgba(129,140,248,0.12)', secondaryHover: 'rgba(78,200,192,0.14)' },
      progress: { lineBg: tokens.surface2, active: tokens.primary, success: tokens.success },
      stage:    { border: tokens.line, text: tokens.muted, labelInactive: '#5B6577' },
      glow:     { primary: 'rgba(129,140,248,0.20)', success: 'rgba(76,195,138,0.18)' },
    } : {
      declined: { bg: '#FEF2F2', border: '#FEE2E2', iconBg: tokens.danger,  title: '#991B1B', text: '#B91C1C' },
      won:      { bg: '#F0FDF4', border: '#DCFCE7', iconBg: tokens.success, title: '#166534', text: '#15803D' },
      buttons:  { primaryHover: 'rgba(79,70,229,0.08)', secondaryHover: 'rgba(14,158,151,0.10)' },
      progress: { lineBg: tokens.surface2, active: tokens.primary, success: tokens.success },
      stage:    { border: tokens.line, text: tokens.muted, labelInactive: '#94A3B8' },
      glow:     { primary: 'rgba(79,70,229,0.15)', success: 'rgba(21,128,61,0.15)' },
    },
  };
};

/* Kurzform in den Overrides: t(theme) → aktive Token-Reihe */
const t = (theme) => theme.palette.tokens;

const kinpointTheme = createTheme({
  /* D3: zwei Farbschemata; die Modus-Wahl (hell/dunkel/system) läuft über
     useColorScheme() und wird zusätzlich in UserPreference gespeichert. */
  colorSchemes: {
    light: { palette: buildPalette(tokensLight, 'light') },
    dark:  { palette: buildPalette(tokensDark, 'dark') },
  },

  shape: { borderRadius: 10 },

  /* Typografie (Design 3.0): Gewichte gedeckelt bei 700 — Eleganz statt
     Plakat; Versalien (overline) kleiner und weniger gesperrt. */
  typography: {
    fontFamily: '"Inter Variable", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '2.5rem',  letterSpacing: '-0.02em',  lineHeight: 1.1 },
    h2: { fontWeight: 700, fontSize: '2rem',    letterSpacing: '-0.015em', lineHeight: 1.15 },
    h3: { fontWeight: 650, fontSize: '1.6rem',  letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, fontSize: '1.95rem', letterSpacing: '-0.015em' },
    h5: { fontWeight: 650, fontSize: '1.35rem', letterSpacing: '-0.01em' },
    h6: { fontWeight: 650, fontSize: '1.12rem', letterSpacing: '0' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.01em' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    overline: { fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.08em' },
  },

  components: {
    /* Obere Leiste */
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: t(theme).surface,
          color: t(theme).ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${t(theme).line}`,
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 12,
        },
        elevation1: { boxShadow: '0 1px 2px rgba(20,24,31,.05), 0 1px 1px rgba(20,24,31,.03)' },
        elevation3: { boxShadow: '0 8px 24px -12px rgba(20,24,31,.18), 0 2px 6px rgba(20,24,31,.05)' },
      },
    },

    /* Seitenleiste */
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: t(theme).sidebar,
          color: t(theme).sidebarText,
          border: 'none',
          borderRight: `1px solid ${t(theme).sidebarLine}`,
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({ color: t(theme).sidebarMuted, minWidth: 36 }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: '0.02em',
          color: t(theme).sidebarText,
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          marginInline: 8,
          '&:hover': { backgroundColor: t(theme).sidebarHover },
          transition: 'background-color .15s ease',
          /* Aktiv (V1): Indigo-Wash auf der dunklen Leiste, aufgehellter Text */
          '&.Mui-selected': {
            backgroundColor: t(theme).sidebarActive,
            '&:hover': { backgroundColor: t(theme).sidebarActive },
            '& .MuiListItemText-primary': { color: t(theme).sidebarActiveText, fontWeight: 650 },
            '& .MuiListItemIcon-root': { color: t(theme).sidebarActiveText },
          },
        }),
      },
    },

    /* Chip (bewährt — unverändert übernommen) */
    MuiChip: {
      defaultProps: { size: 'medium', variant: 'filled', clickable: false },
      styleOverrides: {
        root: ({ theme }) => ({
          userSelect: 'none', WebkitUserSelect: 'none', msUserSelect: 'none',
          height: 32, borderRadius: 12, boxSizing: 'border-box',
          padding: '0 12px', gap: 6,
          fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s ease',
          '&:active': { transform: 'scale(0.95)' },
          '&.Mui-disabled': { opacity: 0.5 },
        }),
        label: { padding: 0, margin: 0 },
        icon: { marginLeft: 0, marginRight: 6, fontSize: '16px' },
        deleteIcon: { marginLeft: 6, marginRight: 0, fontSize: '16px', opacity: 0.6, '&:hover': { opacity: 1 } },
        avatar: { marginLeft: 0, marginRight: 6, width: 20, height: 20, fontSize: '0.75rem' },
        outlined: ({ theme }) => ({ padding: '0 9px', border: `1px solid ${theme.palette.divider}`, backgroundColor: 'transparent' }),
        sizeSmall: { height: 26, fontSize: '0.75rem', padding: '0 8px' },
      },
    },

    /* Datagrid */
    MuiDataGrid: {
      /* D4: Laden als Skeleton statt Spinner — Listen geben nur noch
         ihr `loading` an, der Rest kommt von hier */
      defaultProps: {
        slotProps: { loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${t(theme).line}`,
          backgroundColor: t(theme).surface,
          borderRadius: 14,
          boxShadow: '0 1px 2px rgba(14,26,43,.05)',
          /* Zellinhalte vertikal mittig — für alle Tabellen der App
             (entspricht MUIs eigener „--flex"-Zellvariante; der cell-Slot
             wird von dieser DataGrid-Version nicht als Regel emittiert) */
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 'inherit',
          },
        }),
        /* Kopf ohne Versal-Schrei und ohne Grau-Kachel — Versalien-Diät */
        columnHeader: ({ theme }) => ({
          backgroundColor: t(theme).tableHead,
          color: t(theme).muted,
          fontWeight: 650,
          fontSize: '0.78rem',
          letterSpacing: '0.02em',
        }),
        /* Hover neutral — der Akzent bleibt der echten Auswahl vorbehalten */
        row: ({ theme }) => ({
          transition: 'background-color .15s ease',
          '&:hover': { backgroundColor: t(theme).hoverRow },
          '&.Mui-selected': {
            backgroundColor: t(theme).primarySoft + ' !important',
            '&:hover': { backgroundColor: t(theme).selectedHover + ' !important' },
          },
        }),
        cell: ({ theme }) => ({ borderBottom: `1px solid ${t(theme).lineSoft}`, color: t(theme).ink2 }),
      },
    },

    /* Dialog-Titel: vom schweren dunklen Balken zu ruhiger Kopfzeile */
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: t(theme).surface,
          color: t(theme).ink,
          fontWeight: 700,
          fontSize: '1.15rem',
          padding: '18px 24px',
          borderBottom: `1px solid ${t(theme).line}`,
          marginBottom: 0,
        }),
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        /* Design-3.0-Buttonsprache: Radius 10, flat — Hover vertieft nur
           die Farbe (kein Hüpfen, kein Verlauf mehr) */
        root: {
          textTransform: 'none', fontWeight: 600, borderRadius: 10, paddingInline: 16,
          transition: 'background-color .15s ease, box-shadow .15s ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        },
        /* Primär: satter Vollton mit feiner Lichtkante innen */
        containedPrimary: ({ theme }) => ({
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16), 0 1px 2px rgba(20,24,31,.14)',
          '&:hover': {
            backgroundColor: t(theme).primaryDeep,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,.16), 0 1px 2px rgba(20,24,31,.14)',
          },
          '&:active': { boxShadow: 'none' },
          '&.Mui-disabled': { boxShadow: 'none' },
        }),
        /* Sekundär „tonal": sanft gefüllt statt umrandet (ruhiger als Outlines) */
        outlined: ({ theme }) => ({
          border: 'none',
          backgroundColor: alpha(t(theme).primary, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          '&:hover': { border: 'none', backgroundColor: alpha(t(theme).primary, theme.palette.mode === 'dark' ? 0.24 : 0.16) },
          '&.Mui-disabled': { border: 'none', backgroundColor: alpha(t(theme).ink, 0.05) },
        }),
        outlinedError: ({ theme }) => ({
          backgroundColor: alpha(t(theme).danger, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          '&:hover': { backgroundColor: alpha(t(theme).danger, theme.palette.mode === 'dark' ? 0.24 : 0.16) },
        }),
        sizeLarge: { height: 46, fontSize: '0.95rem' },
      },
    },

    MuiIconButton: {
      variants: [
        {
          props: { variant: 'action-edit' },
          style: ({ theme }) => ({ color: t(theme).primary, '&:hover': { backgroundColor: alpha(t(theme).primary, 0.08) } }),
        },
        {
          props: { variant: 'action-delete' },
          style: ({ theme }) => ({ color: t(theme).danger, '&:hover': { backgroundColor: alpha(t(theme).danger, 0.08) } }),
        },
      ],
    },

    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', minHeight: 48, padding: '6px 12px', fontWeight: 600 },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 8 } },
    },

    /* Required-Stern */
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({ color: t(theme).danger }),
      },
    },
  },
});

export default kinpointTheme;
